// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { ExtendedDatabase } from '@/types/supabase';

// Use environment variables for Supabase configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://jhmzshllqhprqrejmkzv.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpobXpzaGxscWhwcnFyZWpta3p2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI1MDA3MjUsImV4cCI6MjA1ODA3NjcyNX0.e7Wy2M3sQgSj5eudN8Sgt0zl0CAcmJnMuiPZyjhaq1I";

// Configuration for production environment
const getSiteUrl = () => {
  // Check if window is defined (we're in the browser)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    
    // Normalize to the primary domain (remove www if present)
    if (host === 'www.truehr.co') {
      return 'https://truehr.co';
    }
    
    // Check if we're on the production domain
    if (host.includes('truehr.co')) {
      return 'https://truehr.co';
    }
    
    // Check if we're on a Vercel preview deployment
    if (host.includes('vercel.app')) {
      return `https://${host}`;
    }
    
    // Fallback to current origin
    return window.location.origin;
  }
  
  // Fallback for SSR or non-browser environments
  return 'https://truehr.co';
};

// Create a singleton instance of the Supabase client
let supabaseInstance: ReturnType<typeof createClient<ExtendedDatabase>> | null = null;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = (() => {
  if (supabaseInstance) return supabaseInstance;
  
  try {
    console.log("Initializing Supabase client with debug...");
    console.log("Supabase URL:", SUPABASE_URL);
    console.log("Current location:", typeof window !== 'undefined' ? window.location.href : 'SSR');
    console.log("Site URL for auth:", getSiteUrl());
    
    // Fix type issues by explicitly typing the createClient call
    supabaseInstance = createClient<ExtendedDatabase>(
      SUPABASE_URL, 
      SUPABASE_PUBLISHABLE_KEY,
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          storageKey: 'truehr-auth-token',
          storage: typeof window !== 'undefined' ? localStorage : undefined,
          flowType: 'pkce'
        },
        global: {
          headers: {
            'x-client-info': `truehr-website-${typeof window !== 'undefined' ? window.location.hostname : 'server'}`
          }
        },
        realtime: {
          params: {
            eventsPerSecond: 10
          }
        }
      }
    );
    
    // Log connection status for debugging
    if (typeof window !== 'undefined') {
      console.log('Supabase client initialized with URL:', SUPABASE_URL);
      console.log('Current site URL:', getSiteUrl());
      console.log('Hostname:', window.location.hostname);
      console.log('Auth storage ready:', typeof localStorage !== 'undefined');
      
      // Test auth functionality
      supabaseInstance.auth.onAuthStateChange((event, session) => {
        console.log('Supabase auth event:', event, session ? 'with session' : 'no session');
      });
      
      // Immediately check session
      supabaseInstance.auth.getSession().then(({ data, error }) => {
        console.log('Initial session check result:', data.session ? 'Has session' : 'No session');
        if (error) {
          console.error('Session check error:', error);
        }
      });
    }
    
    return supabaseInstance;
  } catch (error) {
    console.error('Failed to initialize Supabase client:', error);
    // Return a mock client for fallback
    return {
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        signUp: () => Promise.resolve({ data: null, error: new Error('Supabase client initialization failed') }),
        signInWithPassword: () => Promise.resolve({ data: null, error: new Error('Supabase client initialization failed') }),
        signOut: () => Promise.resolve({ error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        setSession: () => Promise.resolve({ data: { session: null }, error: null })
      },
      from: () => ({
        select: () => ({
          eq: () => ({
            maybeSingle: () => Promise.resolve({ data: null, error: null }),
            single: () => Promise.resolve({ data: null, error: null })
          }),
        }),
        insert: () => Promise.resolve({ data: null, error: null }),
        update: () => Promise.resolve({ data: null, error: null }),
        delete: () => Promise.resolve({ data: null, error: null }),
      }),
      functions: {
        invoke: () => Promise.resolve({ data: null, error: new Error('Supabase client initialization failed') })
      }
    } as any;
  }
})();

// Export a function to check if we're on the production domain
export const isProductionDomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('truehr.co') || hostname.includes('vercel.app');
};
